import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import { useEffect, useRef, useState, useMemo } from "react";
import { Anchor, Layout, Modal, Tabs, TabsProps } from "antd";
import { ISection, UserGuideProps } from "../types";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../store";
import { fetchChatUserGuide, fetchSupportInfo, fetchWorkflowUserGuide } from "../slices/appInfoSlice";

const { Link } = Anchor;
const { Content, Sider } = Layout;

const MarkdownContent = ({ content }: { content: string }) => (
  <div className="support-content">
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeRaw]}
      className="user-guide-content-style"
    >
      {content || ""}
    </ReactMarkdown>
  </div>
);

const SectionContent = ({ section, contentRefs }: { section: ISection, contentRefs: React.MutableRefObject<{ [key: string]: HTMLDivElement | null }> }) => (
  <div
    key={section.id}
    id={section.id}
    ref={(el) => (contentRefs.current[section.id] = el)}
    className="mb-10"
  >
    <MarkdownContent content={section.content} />
    {section.children.map((child) => (
      <div
        key={child.id}
        id={child.id}
        ref={(el) => (contentRefs.current[child.id] = el)}
        className="mb-10"
      >
        <MarkdownContent content={child.content} />
      </div>
    ))}
  </div>
);

const ScrollableContent = ({ sections }: { sections: ISection[] }) => {
  const contentRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const contentContainerRef = useRef<HTMLDivElement>(null);

  const handleAnchorClick = (e: React.MouseEvent, link: { href: string }) => {
    e.preventDefault();
    const targetId = link.href.split("#")[1];
    const target = contentRefs.current[targetId];
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <Layout className="h-[80vh] bg-transparent">
      <Sider width={200} className="!bg-transparent">
        <Anchor
          className="guide-anchor"
          onClick={handleAnchorClick}
          getContainer={() => contentContainerRef.current || document.body}
        >
          {sections.map((section) => (
            <Link
              href={`#${section.id}`}
              title={section.title}
              key={section.id}
            />
          ))}
        </Anchor>
      </Sider>
      <Content ref={contentContainerRef} className="p-5 overflow-y-auto">
        {sections.map((section) => (
          <SectionContent key={section.id} section={section} contentRefs={contentRefs} />
        ))}
      </Content>
    </Layout>
  );
};

const ClippedContent = ({ sections }: { sections: ISection[] }) => {
  const contentRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const contentContainerRef = useRef<HTMLDivElement>(null);

  return (
    <Content ref={contentContainerRef} className="p-5 overflow-y-auto">
      {sections.map((section) => (
        <SectionContent key={section.id} section={section} contentRefs={contentRefs} />
      ))}
    </Content>
  );
};

const UserGuide = ({ open, onCancel }: UserGuideProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector((state: RootState) => state.user.userDetails);
  const userGuide = useSelector((state: RootState) => state.appInfo.userGuide);
  const [activeKey, setActiveKey] = useState("1");

  const items: TabsProps["items"] = useMemo(() => {
    if (!userGuide) return [];

    const tabItems: TabsProps["items"] = [
      {
        key: "1",
        label: "Chat",
        children: <ScrollableContent sections={userGuide.chat || []} />,
      }
    ];

    if (user?.workflows_enabled && userGuide.workflow) {
      tabItems.push({
        key: "2",
        label: "Workflows",
        children: <ScrollableContent sections={userGuide.workflow} />,
      });
    }

    if (user?.support_tab_enabled && userGuide.support) {
      tabItems.push({
        key: "3",
        label: "Support",
        children: <ClippedContent sections={userGuide.support} />,
      });
    }

    return tabItems;
  }, [user, userGuide]);

  useEffect(() => {
    if (user) {
      const isAdmin = user?.isAdvancedUser ?? false;

      // Fetch data based on user permissions
      dispatch(fetchChatUserGuide({ isAdmin, view: "chat_guide" }));

      if (user.workflows_enabled) {
        dispatch(fetchWorkflowUserGuide({ isAdmin, view: "workflows_guide" }));
      }

      if (user.support_tab_enabled) {
        dispatch(fetchSupportInfo({ isAdmin, view: "support" }));
      }
    }
  }, [dispatch, user]);

  return (
    <Modal
      title="User Guide"
      open={open}
      style={{ top: 20, height: "calc(100vh - 20px)" }}
      width={1280}
      onCancel={onCancel}
      maskClosable={true}
      footer={[
        <button
          key="close"
          className="bg-accent text-white px-4 py-2 rounded"
          onClick={onCancel}
        >
          Close
        </button>,
      ]}
    >
      <Tabs
        activeKey={activeKey}
        onChange={setActiveKey}
        items={items}
        className="user-guide-tab"
      />
    </Modal>
  );
};

export default UserGuide;